import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Badge, Row, Col, Form, ButtonGroup, Dropdown, Nav } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import { bareDate } from '../utils/dates';
import RentalScheduleTab from './RentalScheduleTab';
import RentalProjectionTab from './RentalProjectionTab';
import Pager from './Pager';
import Loading from './Loading';
import { equipmentKind } from '../utils/equipmentLabel';
import { nextBillingDate, billingNote } from '../utils/rentalBilling';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
// Rental dates are bare dates (midnight UTC) — see utils/dates.js.
const dateStr = bareDate;

const STATUS_VARIANT = {
  draft: 'secondary',
  contract_generated: 'info',
  active: 'success',
  completed: 'dark',
  cancelled: 'danger',
};

const STATUS_LABEL = {
  draft: 'Borrador',
  contract_generated: 'Contrato Generado',
  active: 'Activo',
  completed: 'Completado',
  cancelled: 'Cancelado',
};

const PERIOD_LABEL = { daily: 'Diario', weekly: 'Semanal', monthly: 'Mensual' };

const todayYmd = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santo_Domingo' });
// The equipment cards: a line's product (or, without one, its description) and a readable name — the old system's products
// read "SERV. ALQUILER MENSUAL DE CAMA ELECTRICA": each card is a kind of equipment — Camas, Sillas de ruedas,
// Concentradores… (utils/equipmentLabel.js) — so the old and new catalog's names for the same thing count together.
const equipmentKey = (item) => equipmentKind(item.description);

// Each equipment card in its own color (index.css), so the row reads as different things at a glance.
const CARD_COLORS = ['var(--mm-cyan)', 'var(--mm-violet)', 'var(--mm-teal)', 'var(--mm-orange)', 'var(--mm-pink)', 'var(--mm-blue)', 'var(--mm-yellow)', 'var(--mm-green)'];

const isPastDue = (r) => r.status === 'active' && r.endDate && r.endDate.slice(0, 10) < todayYmd();
// Its next factura is due today or is overdue — the same rule as Próximas facturas and the dashboard's brief
// (backend/services/rentalSchedule.js): the next charge is the start date for a contrato generado and the next recharge
// for an active rental, and a period starting on or after the end date isn't charged.
const isDueToInvoice = (r) => { const next = nextBillingDate(r); return !!next && next <= todayYmd(); };
// The list's filters. "Activos y borradores" (what's in play) is the default; a link can open another one with ?filtro=.
const OPEN_STATUSES = ['draft', 'contract_generated', 'active'];
const FILTERS = [
  { key: 'open', label: 'Activos y borradores', test: (r) => OPEN_STATUSES.includes(r.status) },
  { key: 'due', label: 'Por facturar hoy', param: 'por-facturar', test: isDueToInvoice },
  { key: 'past_due', label: 'Vencidos', param: 'vencidos', test: isPastDue },
  { key: 'all', label: 'Todos', param: 'todos', test: () => true },
];
const filterFromUrl = () => {
  const wanted = new URLSearchParams(window.location.search).get('filtro');
  return (FILTERS.find(f => f.param && f.param === wanted) || FILTERS[0]).key;
};

const AlquileresPage = () => {
  const [rentals, setRentals] = useState([]);
  const [loaded, setLoaded] = useState(false); // the first load of the list has come back
  const [search, setSearch] = useState('');
  // ?filtro=vencidos and ?equipo=<tipo> open the list already filtered (the dashboard's links).
  const [filter, setFilter] = useState(filterFromUrl); // a FILTERS key; ?filtro=por-facturar | vencidos | todos
  const [equipment, setEquipment] = useState(() => new URLSearchParams(window.location.search).get('equipo') || null); // an equipment card's key: only active rentals with it
  const [currentPage, setCurrentPage] = useState(1);
  const [actionError, setActionError] = useState('');
  const [returningId, setReturningId] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [notice, setNotice] = useState('');
  const pageSize = 10;
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const isAdmin = user?.role === 'admin';
  // Tabs: the rentals list, "Próximas facturas" (everyone) and "Proyección" (admins — company-wide revenue analytics).
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const tab = requestedTab === 'schedule' || (requestedTab === 'projection' && isAdmin) ? requestedTab : 'list';
  const chooseTab = (key) => setSearchParams(key === 'list' ? {} : { tab: key });

  const loadRentals = () => {
    API.get('/rentals').then(res => setRentals(res.data)).catch(err => console.error('Error loading rentals:', err)).finally(() => setLoaded(true));
  };

  useEffect(loadRentals, []);

  const handleGenerateContract = async (id) => {
    setActionError('');
    setGeneratingId(id);
    try {
      await API.post(`/rentals/${id}/generate-contract`);
      window.open(`/rentals/${id}/print`, '_blank');
      loadRentals();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Error al generar el contrato.');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleReturn = async (id) => {
    setActionError('');
    setReturningId(id);
    try {
      await API.post(`/rentals/${id}/return`);
      loadRentals();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Error al marcar el alquiler como devuelto.');
    } finally {
      setReturningId(null);
    }
  };

  // The conduce that goes with the rental: 'entrega' when the equipment goes out, 'retiro' when it's picked up.
  // Opens its printout to be signed by the client.
  const handleConduce = async (id, kind) => {
    setActionError('');
    setNotice('');
    try {
      const { data } = await API.post(`/rentals/${id}/conduce`, { kind });
      window.open(`/conduces/${data._id}/print`, '_blank');
      setNotice(`Conduce de ${kind} ${data.number} creado.`);
    } catch (err) {
      setActionError(err.response?.data?.message || 'Error al crear el conduce.');
    }
  };

  // For a sales user this is asked of an admin (HTTP 202 — the app-wide toast says so) instead of done.
  const handleCancel = async (r) => {
    if (!window.confirm(`¿Cancelar el borrador ${r.number}?`)) return;
    setActionError('');
    setNotice('');
    try {
      const res = await API.post(`/rentals/${r._id}/cancel`);
      if (res.status !== 202) setNotice(`Alquiler ${r.number} cancelado.`);
      loadRentals();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Error al cancelar el alquiler.');
    }
  };

  // Admins only: for a rental that shouldn't exist (a duplicate, a mistake). The server refuses one that has facturas or
  // conduces, or comes from the old system, and says why; the activity log keeps a snapshot.
  const handleDelete = async (r) => {
    if (!window.confirm(`¿Eliminar el alquiler ${r.number}? No se puede deshacer (queda registrado en Actividad).`)) return;
    setActionError('');
    setNotice('');
    try {
      const { data } = await API.delete(`/rentals/${r._id}`);
      setNotice(data.message || `Alquiler ${r.number} eliminado.`);
      loadRentals();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Error al eliminar el alquiler.');
    }
  };

  const filterCounts = useMemo(() => Object.fromEntries(FILTERS.map(f => [f.key, rentals.filter(f.test).length])), [rentals]);

  const stats = useMemo(() => ({
    activeCount: rentals.filter(r => r.status === 'active').length,
    pastDueCount: rentals.filter(isPastDue).length,
  }), [rentals]);

  const filteredRentals = useMemo(() => {
    let list = rentals;
    if (equipment) list = list.filter(r => r.status === 'active' && (r.items || []).some(i => equipmentKey(i) === equipment));
    const chosen = FILTERS.find(f => f.key === filter) || FILTERS[0];
    list = list.filter(chosen.test);
    // Por facturar hoy: what's due today first, then the overdue ones, oldest first (as on Facturas).
    if (filter === 'due') {
      const today = todayYmd();
      const nextOf = (r) => nextBillingDate(r) || '';
      list = [...list].sort((a, b) => {
        const da = nextOf(a); const db = nextOf(b);
        return ((da >= today) === (db >= today) ? 0 : da >= today ? -1 : 1) || da.localeCompare(db);
      });
    }
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter(r =>
      r.number?.toLowerCase().includes(term) ||
      r.client?.name?.toLowerCase().includes(term)
    );
  }, [rentals, search, filter, equipment]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filter, equipment]);

  // One card per kind of equipment out on an active rental: how many rentals have it and how many units are out, most
  // rented first. Grouped by product (a line without one, by its description) — the products' categories don't tell them
  // apart (the old system's are all "Alquiler").
  const equipmentCards = useMemo(() => {
    const groups = new Map();
    for (const r of rentals) {
      if (r.status !== 'active') continue;
      for (const item of r.items || []) {
        const key = equipmentKey(item);
        if (!key) continue;
        if (!groups.has(key)) groups.set(key, { key, rentals: new Set(), units: 0 });
        const g = groups.get(key);
        g.rentals.add(r._id);
        g.units += Number(item.quantity) || 0;
      }
    }
    return [...groups.values()].map(g => ({ ...g, rentals: g.rentals.size, label: g.key }))
      .sort((a, b) => b.rentals - a.rentals || a.label.localeCompare(b.label));
  }, [rentals]);
  const chosenEquipment = equipmentCards.find(c => c.key === equipment);

  const totalPages = Math.max(1, Math.ceil(filteredRentals.length / pageSize));
  const pagedRentals = filteredRentals.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Like Manifold Manager: nothing but the loading indicator until the page's data has come back.
  if (!loaded) return <Loading page label="Cargando alquileres..." />;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="app-title" style={{ textAlign: 'left', margin: 0 }}>Alquileres</h2>
        <Button onClick={() => navigate('/rentals/new')}>+ Nuevo Alquiler</Button>
      </div>

      <Nav variant="tabs" activeKey={tab} onSelect={chooseTab} className="mb-3">
        <Nav.Item><Nav.Link eventKey="list"><i className="bi bi-list-ul me-1" />Alquileres</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="schedule"><i className="bi bi-calendar-event me-1" />Próximas facturas</Nav.Link></Nav.Item>
        {isAdmin && <Nav.Item><Nav.Link eventKey="projection"><i className="bi bi-graph-up-arrow me-1" />Proyección</Nav.Link></Nav.Item>}
      </Nav>

      {tab === 'schedule' && <RentalScheduleTab />}
      {tab === 'projection' && <RentalProjectionTab />}
      {tab === 'list' && (<>

      <Row className="mb-3">
        <Col md={6}>
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Alquileres Activos</div>
            <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-teal)' }}>{stats.activeCount}</h2>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Vencidos (devolución)</div>
            <h2 className="mt-2 mb-0" style={{ color: stats.pastDueCount > 0 ? 'var(--mm-red)' : undefined }}>{stats.pastDueCount}</h2>
          </Card>
        </Col>
      </Row>

      {equipmentCards.length > 0 && (
        <Row className="mb-2">
          {equipmentCards.map((c, i) => {
            const color = CARD_COLORS[i % CARD_COLORS.length];
            return (
            <Col key={c.key} xs={6} md={4} lg={3} xl={2} className="mb-2">
              <Card role="button" title={`${c.label} — clic para ver estos alquileres`}
                className={`shadow-sm p-2 px-3 h-100 dashboard-card dashboard-card-link`}
                style={{ borderTop: `3px solid ${color}`, ...(equipment === c.key ? { borderColor: color, boxShadow: `0 0 0 1px ${color}` } : {}) }}
                onClick={() => setEquipment(equipment === c.key ? null : c.key)}>
                <div className="text-muted small text-truncate">{c.label}</div>
                <div className="d-flex align-items-baseline gap-2">
                  <h3 className="mb-0" style={{ color }}>{c.rentals}</h3>
                  <span className="small text-muted">{c.rentals === 1 ? 'alquiler' : 'alquileres'}</span>
                </div>
                {c.units !== c.rentals && <div className="small text-muted">{c.units} unidades</div>}
              </Card>
            </Col>
            );
          })}
        </Row>
      )}

      <Card className="shadow-sm p-3">
        {chosenEquipment && (
          <div className="alert alert-info d-flex justify-content-between align-items-center py-2">
            <span>Alquileres activos con <strong>{chosenEquipment.label}</strong> ({chosenEquipment.rentals})</span>
            <Button size="sm" variant="outline-secondary" onClick={() => setEquipment(null)}>Quitar filtro</Button>
          </div>
        )}
        {actionError && <div className="alert alert-danger">{actionError}</div>}
        {notice && <div className="alert alert-success">{notice}</div>}
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <Form.Control
            style={{ maxWidth: 320 }}
            placeholder="Buscar por número o cliente..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <ButtonGroup size="sm">
            {FILTERS.map(f => (
              <Button key={f.key} variant={filter === f.key ? 'primary' : 'outline-secondary'} onClick={() => setFilter(f.key)}>
                {f.label}{f.key !== 'all' && <span className="ms-1" style={{ opacity: 0.8 }}>({filterCounts[f.key]})</span>}
              </Button>
            ))}
          </ButtonGroup>
        </div>
        <Table hover responsive className="mb-0">
          <thead>
            <tr>
              <th>No.</th><th>Cliente</th><th>Equipo</th><th>Período</th>
              <th>Inicio</th><th title="Cuándo toca hacer la próxima factura del alquiler">Facturar</th><th className="text-end">Monto/Ciclo</th><th>Estado</th><th></th>
            </tr>
          </thead>
          <tbody>
            {pagedRentals.map(r => {
              const pastDue = isPastDue(r);
              // The "Facturar" column (it replaced "Fin": these rentals have no end date): when the next factura is due.
              const nextBill = nextBillingDate(r);
              const billNote = billingNote(nextBill);
              return (
                <tr key={r._id}>
                  <td>{r.number}</td>
                  <td>{r.client?.name || '—'}</td>
                  <td>{(r.items || []).map(i => i.description).join(', ') || '—'}</td>
                  <td>{PERIOD_LABEL[r.billingPeriod] || r.billingPeriod}</td>
                  <td>{dateStr(r.startDate)}</td>
                  <td className="text-nowrap">
                    {nextBill ? dateStr(nextBill) : '—'}
                    {billNote && <div className="small" style={{ color: billNote.color }}>{billNote.text}</div>}
                    {pastDue && <div className="small" style={{ color: 'var(--mm-red)' }}>debía devolverse el {dateStr(r.endDate)}</div>}
                  </td>
                  <td className="text-end">{money(r.total)}</td>
                  <td><Badge bg={STATUS_VARIANT[r.status]}>{STATUS_LABEL[r.status] || r.status}</Badge></td>
                  <td className="text-end">
                    <div className="d-flex gap-2 justify-content-end">
                      {r.status === 'draft' && (
                        <>
                          <Button size="sm" variant="outline-secondary" onClick={() => navigate(`/rentals/${r._id}/edit`)}>
                            <i className="bi bi-pencil" /> Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            disabled={generatingId === r._id}
                            onClick={() => handleGenerateContract(r._id)}
                          >
                            {generatingId === r._id ? 'Generando...' : 'Generar Contrato'}
                          </Button>
                        </>
                      )}
                      {['contract_generated', 'active'].includes(r.status) && (
                        <Button size="sm" variant="outline-primary" onClick={() => navigate(`/invoices/new?rental=${r._id}`)}
                          title={r.status === 'active' && r.nextRechargeDate ? `Factura del período que empieza el ${dateStr(r.nextRechargeDate)}` : 'Primera factura del alquiler'}>
                          {r.status === 'active' ? 'Renovar alquiler' : 'Facturar'}
                        </Button>
                      )}
                      {r.status === 'active' && (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={returningId === r._id}
                          onClick={() => handleReturn(r._id)}
                        >
                          {returningId === r._id ? 'Guardando...' : 'Marcar Devuelto'}
                        </Button>
                      )}
                      {r.status !== 'draft' && (
                        <Button size="sm" variant="outline-secondary" onClick={() => window.open(`/rentals/${r._id}/print`, '_blank')}>
                          <i className="bi bi-printer" /> Ver
                        </Button>
                      )}
                      {r.status !== 'cancelled' || isAdmin ? (
                        <Dropdown align="end">
                          <Dropdown.Toggle size="sm" variant="outline-secondary" aria-label="Más acciones">
                            <i className="bi bi-three-dots" />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {['contract_generated', 'active'].includes(r.status) && (
                              <Dropdown.Item onClick={() => handleConduce(r._id, 'entrega')}>
                                <i className="bi bi-truck" /> Conduce de entrega
                              </Dropdown.Item>
                            )}
                            {['active', 'completed'].includes(r.status) && (
                              <Dropdown.Item onClick={() => handleConduce(r._id, 'retiro')}>
                                <i className="bi bi-box-arrow-in-left" /> Conduce de retiro
                              </Dropdown.Item>
                            )}
                            {/* Only a borrador is cancelled; an active rental ends with Marcar devuelto. */}
                            {r.status === 'draft' && (
                              <Dropdown.Item className="text-danger" onClick={() => handleCancel(r)}>
                                <i className="bi bi-x-circle" /> Cancelar borrador
                              </Dropdown.Item>
                            )}
                            {isAdmin && (
                              <Dropdown.Item className="text-danger" onClick={() => handleDelete(r)}>
                                <i className="bi bi-trash" /> Eliminar alquiler
                              </Dropdown.Item>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredRentals.length === 0 && (
              <tr><td colSpan={9} className="text-muted text-center">
                {!loaded ? <Loading label="Cargando alquileres..." /> : search ? `Ningún alquiler coincide con "${search}".` : 'Sin alquileres todavía.'}
              </td></tr>
            )}
          </tbody>
        </Table>

        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="text-muted small">
              Página {currentPage} de {totalPages} — {filteredRentals.length} alquiler{filteredRentals.length === 1 ? '' : 'es'}
            </span>
            <Pager page={currentPage} pages={totalPages} onChange={setCurrentPage} showEnds />
          </div>
        )}
      </Card>
      </>)}
    </div>
  );
};

export default AlquileresPage;
